import React, {
  Children,
  useState,
  cloneElement,
  useEffect,
  useRef,
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import {
  MdOutlineCheckCircle,
  MdOutlineChecklist,
  MdOutlineList,
} from 'react-icons/md';
import { PropTypes } from 'prop-types';
import { CiCircleCheck } from 'react-icons/ci';
import theme from '@/common/theme';

const ToggleButtonWrapper = styled.div`
  position: relative;
`;

// Styled component for the menu container
const MenuContainer = styled.div`
  & > *:not(:first-child) {
    background-color: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    z-index: 1;
  }
`;

// Styled component for the menu items container
const MenuItemsContainer = styled.div`
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  position: absolute;
  z-index: 1;
  width: max-content;
  margin-top: 6px;
`;

// Styled component for the menu items
const MenuItem = styled.div`
  padding: 8px;
  @media (hover: hover) {
    &:hover {
      background-color: #f5f5f5;
    }
  }
`;

const MultiSingleLabel = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: ${({ $isHidden }) =>
    $isHidden ? theme.COLOR_GREY4 : theme.COLOR_GREY_TEXT};
  background-color: ${({ $isHidden }) =>
    $isHidden ? theme.COLOR_GREY5 : 'initial'};
  font-size: 14px;
  width: auto;
  height: 24px;
  border-radius: 2px;
  border: 1px solid ${() => theme.COLOR_GREY5};
  gap: 4px;
  padding: 7px 11px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    pointer-events: none;
  }
`;

const MultiSingleLabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  white-space: nowrap;
`;

const StickyMenuField = styled.div`
  display: flex;
`;

const StickyMenuIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  padding: 0 0;

  span {
    padding: 0 0;
  }
`;

const StickyMenuTitle = styled.span`
  margin: 0 0 0 5px;
  display: flex;
  position: relative;
`;

/**
 * Item component for displaying the selection type dropdown menu.
 * @param {object} props - The props passed to the OptionMenu component.
 * @param {Array} props.children - An array of menu items.
 * @param {Array} props.toggleButton - An array of menu items.
 * @param {boolean} props.disabled - Whether the option is disabled.
 * @returns {JSX.Element} The rendered Item component.
 */
const SelectionTypeInput = ({ children, toggleButton, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the selection type menu container
      const isOutsideContainer =
        menuRef.current && !menuRef.current.contains(event.target);

      // Check if click is on a selection type menu item (portal content)
      const isSelectionTypeMenuItem =
        event.target.closest('[data-selection-type-menu-item]') ||
        event.target.closest('.selection-type-menu-item') ||
        event.target.closest('[role="menuitem"]');

      // Only close if clicking outside AND not on a selection type menu item
      if (isOutsideContainer && !isSelectionTypeMenuItem) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const handleMenuItemClick = () => {
    setPosition({ x: 0, y: 0 });
    setIsOpen(false);
  };

  return (
    <MenuContainer ref={menuRef}>
      {/* Toggle button */}
      <ToggleButtonWrapper>
        {toggleButton &&
          cloneElement(toggleButton, {
            onClick: (e) => {
              e.preventDefault();
              if (!disabled) {
                setIsOpen(!isOpen);
              }
            },
          })}
      </ToggleButtonWrapper>

      {isOpen && (
        <MenuItemsContainer $position={position}>
          {Children.map(children, (child) => (
            <MenuItem
              role="menuitem"
              className="selection-type-menu-item"
              onClick={handleMenuItemClick}
            >
              {child}
            </MenuItem>
          ))}
        </MenuItemsContainer>
      )}
    </MenuContainer>
  );
};

SelectionTypeInput.defaultProps = {
  children: null,
  toggleButton: null,
  disabled: false,
};
SelectionTypeInput.propTypes = {
  children: PropTypes.array,
  toggleButton: PropTypes.object,
  disabled: PropTypes.bool,
};

const MultiselectButton = ({ selectionType, onClick, isHidden, disabled }) => {
  const isMultipleSelection = selectionType?.includes('multi');
  const isDropdown = selectionType?.includes('dropdown');
  let label = 'Single selection';

  if (isMultipleSelection) {
    label = 'Multiple selection';
  } else if (isDropdown) {
    label = 'Display Dropdown';
  }

  return (
    <MultiSingleLabel
      disabled={disabled}
      role="button"
      type="button"
      onClick={!disabled ? onClick : undefined}
      $isHidden={isHidden}
    >
      <CiCircleCheck color={isHidden ? theme.COLOR_GREY4 : 'inherit'} />
      {label}
    </MultiSingleLabel>
  );
};

MultiselectButton.defaultProps = {
  onClick: null,
  isHidden: false,
  disabled: false,
};

MultiselectButton.propTypes = {
  selectionType: PropTypes.string.isRequired,
  onClick: PropTypes.string,
  isHidden: PropTypes.bool,
  disabled: PropTypes.bool,
};

/**
 * Item component for displaying an multisingle select.
 * @param {object} props - The props passed to the OptionMenu component.
 * @param {Array} props.name - An array of menu items.
 * @param {boolean} props.isHidden - Whether the option is hidden.
 * @param {boolean} props.disabled - Whether the option is disabled.
 * @param {boolean} props.isFolder - Whether the option is a folder.
 * @returns {JSX.Element} The rendered Item component.
 */
export const MultiSingleSelect = ({ name, isHidden, disabled, isFolder }) => {
  const { control } = useFormContext();

  return (
    <MultiSingleLabelWrapper>
      <Controller
        control={control}
        name={`${name}.select_type`}
        render={({ field }) => (
          <SelectionTypeInput
            disabled={disabled}
            toggleButton={
              <MultiselectButton
                selectionType={field.value}
                isHidden={isHidden}
                disabled={disabled}
              />
            }
          >
            <StickyMenuField onClick={() => field.onChange('single')}>
              <StickyMenuIcon>
                <MdOutlineCheckCircle />
              </StickyMenuIcon>
              <StickyMenuTitle>Single Selection</StickyMenuTitle>
            </StickyMenuField>
            <StickyMenuField onClick={() => field.onChange('multi')}>
              <StickyMenuIcon>
                <MdOutlineChecklist />
              </StickyMenuIcon>
              <StickyMenuTitle>Multiple Selection</StickyMenuTitle>
            </StickyMenuField>
            {isFolder && (
              <StickyMenuField onClick={() => field.onChange('dropdown')}>
                <StickyMenuIcon>
                  <MdOutlineList />
                </StickyMenuIcon>
                <StickyMenuTitle>Display Dropdown</StickyMenuTitle>
              </StickyMenuField>
            )}
          </SelectionTypeInput>
        )}
      />
    </MultiSingleLabelWrapper>
  );
};

MultiSingleSelect.defaultProps = {
  isHidden: false,
  disabled: false,
  isFolder: false,
};

MultiSingleSelect.propTypes = {
  name: PropTypes.string.isRequired,
  isHidden: PropTypes.bool,
  disabled: PropTypes.bool,
  isFolder: PropTypes.bool,
};

export default SelectionTypeInput;
